<template>
  <DefaultCrudView
    @create-new="newBillable"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="billables"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.active`]="{ item }">
          <v-chip small :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:[`item.price`]="{ item }">
          {{ formatPrice(item.price) }}
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editBillable(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item v-if="item.active" @click="toggleActive(item, false)">
                <v-list-item-icon>
                  <v-icon medium>mdi-currency-usd-off</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('deactivate_billable') }}</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.active" @click="toggleActive(item, true)">
                <v-list-item-icon>
                  <v-icon medium>mdi-currency-usd</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('activate_billable') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item @click="openDeliverableBillableModal(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-package-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_deliverables') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DeliverableBillableModal ref="deliverableBillableModal"></DeliverableBillableModal>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import DeliverableBillableModal from '@/components/business/DeliverableBillableModal.vue'

import AppActions from '@/store/app/actions-types'

import BillableAPI from '@/services/api/business/billable'

export default {
  name: 'CustomerBillables',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    DeliverableBillableModal,
  },

  computed: {
    customerId() {
      return this.$route.params.customerId
    },

    customerName() {
      return this.$route.query.customerName || this.customerId
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'DeliverableSettings',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customers'),
          prependIcon: 'mdi-account-group',
          to: {
            name: 'BusinessCustomers',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customer_billables') + (this.customerName ? ': ' + this.customerName : ''),
          prependIcon: 'mdi-cash-multiple',
        },
      ]
    },

    fields() {
      if (this.selectedBillable) {
        // Edit mode: billable_title + unit_label + price + active
        return [
          {
            key: 'billable_title',
            name: 'billable_title',
            label: this.$t('billable_title'),
            type: 'text',
            value: this.selectedBillable.billable_title,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 12,
          },
          {
            key: 'unit_label',
            name: 'unit_label',
            label: this.$t('unit_label'),
            type: 'text',
            value: this.selectedBillable.unit_label,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 6,
          },
          {
            key: 'price',
            name: 'price',
            label: this.$t('price'),
            type: 'text',
            value: this.selectedBillable.price,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 6,
          },
          {
            key: 'active',
            name: 'active',
            label: this.$t('active'),
            type: 'switch',
            value: this.selectedBillable.active,
            cols: 12,
          },
        ]
      }

      // Create mode: billable_title + unit_label + price
      return [
        {
          key: 'billable_title',
          name: 'billable_title',
          label: this.$t('billable_title'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12,
        },
        {
          key: 'unit_label',
          name: 'unit_label',
          label: this.$t('unit_label'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6,
        },
        {
          key: 'price',
          name: 'price',
          label: this.$t('price'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 6,
        },
      ]
    },
  },

  data() {
    return {
      loading: true,
      billables: [],
      options: {
        sortBy: ['billable_title'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_billable',
          eventToEmit: 'create-new',
        },
      ],
      selectedBillable: null,
      headers: [
        { text: this.$t('billable_title'), value: 'billable_title', sortable: true },
        { text: this.$t('unit_label'), value: 'unit_label', sortable: true },
        { text: this.$t('price'), value: 'price', sortable: true },
        { text: this.$t('active'), value: 'active', sortable: true },
        { text: this.$t('created_at'), value: 'created_at', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedBillable = null
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    openDeliverableBillableModal(billable) {
      this.$refs.deliverableBillableModal.openModal('from-billable', billable)
    },

    newBillable() {
      this.selectedBillable = null
      this.$refs.modal.openModal()
    },

    editBillable(billable) {
      this.selectedBillable = billable
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        formData[field.name] = field.value
      })
      const normalizedPrice = this.normalizePrice(formData.price)

      let apiCall

      if (this.selectedBillable) {
        const billableId = this.selectedBillable.billable_id

        if (!billableId) {
          console.error('Billable ID not found in selected billable object:', this.selectedBillable)
          this.$refs.modal.errorMessage = this.$t('billable_id_not_found')
          return
        }

        apiCall = BillableAPI.updateBillable(billableId, {
          billable_title: formData.billable_title,
          unit_label: formData.unit_label,
          price: normalizedPrice,
          active: formData.active,
        })
      } else {
        apiCall = BillableAPI.createBillable({
          customer_id: this.customerId,
          billable_title: formData.billable_title,
          unit_label: formData.unit_label,
          price: normalizedPrice,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedBillable
            ? this.$t('billable_updated_successfully')
            : this.$t('billable_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    toggleActive(billable, activeValue) {
      const billableId = billable.billable_id

      if (!billableId) {
        console.error('Billable ID not found:', billable)
        return
      }

      BillableAPI.updateBillable(billableId, {
        billable_title: billable.billable_title,
        unit_label: billable.unit_label,
        price: billable.price,
        active: activeValue,
      })
        .then(() => {
          const message = activeValue
            ? this.$t('billable_activated_successfully')
            : this.$t('billable_deactivated_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error updating billable active status:', error)
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      // Remove customerName from query params before sending to API
      delete options.customerName

      this.loading = true
      BillableAPI.getAllBillablesByCustomer(this.customerId, options)
        .then((result) => {
          this.billables = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    getModalTitle() {
      return this.selectedBillable ? this.$t('edit_billable') : this.$t('new_billable')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },

    normalizePrice(price) {
      if (price === null || price === undefined) return price

      const rawPrice = String(price).trim()
      if (rawPrice === '') return rawPrice

      const sanitizedPrice = rawPrice.replace(/\s/g, '')
      const lastComma = sanitizedPrice.lastIndexOf(',')
      const lastDot = sanitizedPrice.lastIndexOf('.')

      if (lastComma !== -1 && lastDot !== -1) {
        const decimalSeparator = lastComma > lastDot ? ',' : '.'
        const thousandsSeparator = decimalSeparator === ',' ? '.' : ','

        return sanitizedPrice
          .split(thousandsSeparator)
          .join('')
          .replace(decimalSeparator, '.')
      }

      if (lastComma !== -1) {
        const integerPart = sanitizedPrice.slice(0, lastComma).replace(/,/g, '')
        const decimalPart = sanitizedPrice.slice(lastComma + 1).replace(/,/g, '')
        return `${integerPart}.${decimalPart}`
      }

      return sanitizedPrice
    },

    formatPrice(price) {
      if (price === null || price === undefined) return ''

      const normalizedPrice = this.normalizePrice(price)
      if (normalizedPrice === '') return ''

      const parsedPrice = Number(normalizedPrice)
      if (Number.isNaN(parsedPrice)) return price

      return parsedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },
}
</script>
